import { useEffect, useState } from 'react'
import { Check, ChevronDown, MessageSquare, Plus } from 'lucide-react'
import type { ChatConversation } from '@one-deployment/protocol'
import { Button } from '@/components/shadcn/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/shadcn/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/cn'

/** "3m", "5h", "2d": how long ago, short enough for a list row. */
export function since(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000))
  if (s < 60) return 'now'
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86_400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86_400)}d`
}

/**
 * The session's conversations, above the chat: which one Claude runs, a list
 * of the earlier ones to go back into, and a new one. Switching restarts the
 * session's Claude on that conversation; the tmux session stays.
 */
export function ConversationBar({ items, answered, canSwitch, onOpen, onSwitch, compact = false, hideNew = false }: {
  items: ChatConversation[]; answered: boolean; canSwitch: boolean; onOpen: () => void; onSwitch: (id?: string) => void
  /** In a tab header: one small row, no border of its own. */
  compact?: boolean
  /** New chat lives elsewhere (the window menu's plus). */
  hideNew?: boolean
}) {
  const [open, setOpen] = useState(false)
  // A box that predates conversations never answers; say so rather than spin.
  const [gaveUp, setGaveUp] = useState(false)
  useEffect(() => {
    if (answered || !open) return
    setGaveUp(false)
    const t = setTimeout(() => setGaveUp(true), 5000)
    return () => clearTimeout(t)
  }, [answered, open])
  const current = items.find((c) => c.current)
  const empty = !answered ? (gaveUp ? 'This server cannot list conversations yet; it updates shortly' : 'Loading…') : items.length ? 'No match' : 'No conversations yet'
  return (
    <div className={cn('flex shrink-0 items-center', compact ? 'h-full min-w-0 gap-1' : 'h-9 gap-2 border-b px-3')}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) onOpen() }}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className={cn('min-w-0 justify-start gap-1.5 text-xs font-normal', compact ? 'h-6 rounded-sm px-1.5 text-fg-subtle hover:bg-hover' : 'h-7 max-w-[60%] px-2')} aria-label="Conversations" title={current?.title ?? 'Conversations'}>
            <MessageSquare className="size-3.5 text-muted-foreground" />
            {!compact && <span className="truncate">{current?.title ?? 'Conversation'}</span>}
            <ChevronDown className="size-3 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align={compact ? 'end' : 'start'} className="w-96 p-0">
          <Command>
            <CommandInput placeholder="Find a conversation…" className="h-9 text-xs" />
            <CommandList className="max-h-80">
              <CommandEmpty className="py-6 text-center text-xs">{empty}</CommandEmpty>
              <CommandGroup heading="Conversations in this session">
                {items.map((c) => (
                  <CommandItem key={c.id} value={c.id} keywords={[c.title]} disabled={!canSwitch && !c.current}
                    onSelect={() => { setOpen(false); if (!c.current) onSwitch(c.id) }} className="gap-2 text-xs">
                    <Check className={cn('size-3.5 shrink-0', c.current ? 'opacity-100' : 'opacity-0')} />
                    <span className="min-w-0 flex-1 truncate">{c.title}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{since(c.updated_at)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {!hideNew && <Button variant={compact ? 'ghost' : 'outline'} size="sm" className={cn('gap-1 text-xs', compact ? 'h-5 rounded-xs px-1.5 font-normal text-fg-subtle hover:bg-hover' : 'ml-auto h-7 px-2')} disabled={!canSwitch} onClick={() => onSwitch()}>
        <Plus className="size-3.5" />New chat
      </Button>}
    </div>
  )
}
